import type {
  AutocompleteResponse,
  SearchResponse,
  ResolveCardsRequest,
  ResolveCardsResponse,
  ProfileDeckRequest,
  ProfileDeckResponse,
  SymbolMap,
  ApiError,
  TypeCatalogResponse,
  SetCatalogResponse,
  BracketRequest,
  BracketResponse,
  CardCombosResponse,
  CardEdhrecResponse,
  DirectionsRequest,
  DirectionsResponse,
  MoxfieldResolveRequest,
  MoxfieldResolveResponse,
  RecommanderResponse,
  MdfcCandidatesResponse,
  Color,
  CollectionDeck,
  CollectionDeckSummary,
  AnalyticsOverview,
  ColorIdentityCoverage,
  TagCoverageItem,
  StrategyGap,
  DeckStrategyProfile,
  MoxfieldUserDeck,
  SwipeDeckResponse,
  BrowseResponse,
  VerdictRequest,
  CommanderVerdict,
  GlobalThemesResponse,
  SetReviewCardsResponse,
  GetSessionResponse,
  CreateSessionRequest,
  PatchSessionRequest,
  CommitSessionResponse,
} from '@mtg-deck-tool/shared';

export type {
  CollectionDeck,
  CollectionDeckSummary,
  AnalyticsOverview,
  ColorIdentityCoverage,
  TagCoverageItem,
  StrategyGap,
  DeckStrategyProfile,
  MoxfieldUserDeck,
  SwipeDeckResponse,
  VerdictRequest,
  CommanderVerdict,
  GlobalThemesResponse,
};


import { auth } from './firebase.js';

// ─── Error ────────────────────────────────────────────────────────────────────

export class ApiClientError extends Error {
  constructor(
    public readonly code: ApiError['code'],
    message: string,
    public readonly status: number,
  ) {
    super(message);
    this.name = 'ApiClientError';
  }
}

// ─── Base fetch ───────────────────────────────────────────────────────────────

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const headers = new Headers(init?.headers);
  if (init?.body !== undefined && init?.body !== null) {
    headers.set('Content-Type', 'application/json');
  }

  const currentUser = auth.currentUser;
  if (currentUser) {
    try {
      const token = await currentUser.getIdToken();
      headers.set('Authorization', `Bearer ${token}`);
    } catch (err) {
      console.error('[api-client] Failed to get auth token:', err);
    }
  }

  const res = await fetch(path, {
    ...init,
    headers,
  });

  if (!res.ok) {
    let code: ApiError['code'] = 'internal_error';
    let message = res.statusText;
    try {
      const body = (await res.json()) as ApiError;
      code = body.code ?? code;
      message = body.message ?? message;
    } catch {
      // ignore parse error
    }
    throw new ApiClientError(code, message, res.status);
  }

  // 204 No Content (and any 2xx with no body): resolve undefined without parsing.
  // Verdict POST and DELETE return 204 — calling res.json() on an empty body throws.
  if (res.status === 204) {
    return undefined as T;
  }
  const contentLength = res.headers.get('content-length');
  const contentType = res.headers.get('content-type') ?? '';
  if (contentLength === '0' || !contentType.includes('application/json')) {
    // Non-JSON or explicitly empty body — return undefined rather than throwing
    if (contentLength === '0') return undefined as T;
  }

  return res.json() as Promise<T>;
}

// ─── API methods ──────────────────────────────────────────────────────────────

export const api = {
  autocomplete(q: string): Promise<AutocompleteResponse> {
    return request<AutocompleteResponse>(`/api/cards/autocomplete?q=${encodeURIComponent(q)}`);
  },

  search(
    q: string,
    opts: { order?: string; dir?: string; page?: number } = {},
  ): Promise<SearchResponse> {
    const params = new URLSearchParams({ q });
    if (opts.order) params.set('order', opts.order);
    if (opts.dir) params.set('dir', opts.dir);
    if (opts.page) params.set('page', String(opts.page));
    return request<SearchResponse>(`/api/cards/search?${params.toString()}`);
  },

  resolveCards(cardNames: string[]): Promise<ResolveCardsResponse> {
    return request<ResolveCardsResponse>('/api/cards/resolve', {
      method: 'POST',
      body: JSON.stringify({ cardNames } satisfies ResolveCardsRequest),
    });
  },

  profileDeck(cardNames: string[], force = false): Promise<ProfileDeckResponse> {
    return request<ProfileDeckResponse>('/api/deck/profile', {
      method: 'POST',
      body: JSON.stringify({ cardNames, force }),
    });
  },

  getSymbols(): Promise<SymbolMap> {
    return request<SymbolMap>('/api/symbols');
  },

  getCatalogTypes(): Promise<TypeCatalogResponse> {
    return request<TypeCatalogResponse>('/api/catalog/types');
  },

  getCatalogSets(q: string): Promise<SetCatalogResponse> {
    return request<SetCatalogResponse>(`/api/catalog/sets?q=${encodeURIComponent(q)}`);
  },

  evaluateBracket(cardNames: string[]): Promise<BracketResponse> {
    return request<BracketResponse>('/api/deck/bracket', {
      method: 'POST',
      body: JSON.stringify({ cardNames } satisfies BracketRequest),
    });
  },

  getCardCombos(name: string): Promise<CardCombosResponse> {
    return request<CardCombosResponse>(`/api/cards/combos?name=${encodeURIComponent(name)}`);
  },

  getEdhrecCards(name: string, isCommander: boolean): Promise<CardEdhrecResponse> {
    const params = new URLSearchParams({ name, isCommander: String(isCommander) });
    return request<CardEdhrecResponse>(`/api/cards/edhrec?${params.toString()}`);
  },

  fetchDirections(
    commanderNames: string[],
    deckTagCounts: Array<{ slug: string; matchCount: number }>,
  ): Promise<DirectionsResponse> {
    return request<DirectionsResponse>('/api/deck/directions', {
      method: 'POST',
      body: JSON.stringify({
        commanderNames,
        deckTagCounts,
      } satisfies DirectionsRequest),
    });
  },

  resolveMoxfieldDeck(deckId: string): Promise<MoxfieldResolveResponse> {
    return request<MoxfieldResolveResponse>('/api/moxfield/resolve', {
      method: 'POST',
      body: JSON.stringify({ deckId } satisfies MoxfieldResolveRequest),
    });
  },

  getRecommendations(source: string, deckId: string): Promise<RecommanderResponse> {
    return request<RecommanderResponse>(
      `/api/recommander/${encodeURIComponent(source)}/${encodeURIComponent(deckId)}`,
    );
  },

  getMdfcCandidates(colors: Color[]): Promise<MdfcCandidatesResponse> {
    const params = new URLSearchParams({ colors: colors.join('') });
    return request<MdfcCandidatesResponse>(`/api/land/mdfc-candidates?${params.toString()}`);
  },

  getCollectionDecks(): Promise<CollectionDeckSummary[]> {
    return request<CollectionDeckSummary[]>('/api/collection/decks');
  },

  getCollectionDeck(id: string): Promise<CollectionDeck> {
    return request<CollectionDeck>(`/api/collection/decks/${encodeURIComponent(id)}`);
  },

  saveCollectionDeck(deck: any): Promise<void> {
    return request<void>('/api/collection/decks', {
      method: 'POST',
      body: JSON.stringify(deck),
    });
  },

  deleteCollectionDeck(id: string): Promise<void> {
    return request<void>(`/api/collection/decks/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    });
  },

  deleteCollectionDecks(ids: string[]): Promise<void> {
    return request<void>('/api/collection/decks/bulk-delete', {
      method: 'POST',
      body: JSON.stringify({ ids }),
    });
  },

  syncCollectionDeck(id: string): Promise<CollectionDeck> {
    return request<CollectionDeck>(`/api/collection/decks/${encodeURIComponent(id)}/sync`, {
      method: 'POST',
    });
  },

  importMoxfieldDeck(moxfieldDeckId: string): Promise<CollectionDeck> {
    return request<CollectionDeck>('/api/collection/import/moxfield', {
      method: 'POST',
      body: JSON.stringify({ moxfieldDeckId }),
    });
  },

  purgeCache(): Promise<{ status: string; purged: { tagProfiles: number; edhrecThemes: number; edhrecSynergies: number } }> {
    return request('/api/collection/purge-cache', {
      method: 'POST',
    });
  },

  getAnalyticsOverview(): Promise<AnalyticsOverview> {
    return request<AnalyticsOverview>('/api/analytics/overview');
  },

  getColorCoverage(): Promise<ColorIdentityCoverage[]> {
    return request<ColorIdentityCoverage[]>('/api/analytics/colors');
  },

  getTagCoverage(): Promise<TagCoverageItem[]> {
    return request<TagCoverageItem[]>('/api/analytics/tags');
  },

  getStrategyGaps(): Promise<StrategyGap[]> {
    return request<StrategyGap[]>('/api/analytics/gaps');
  },

  getDeckStrategies(): Promise<DeckStrategyProfile[]> {
    return request<DeckStrategyProfile[]>('/api/analytics/deck-strategies');
  },

  getMoxfieldUserDecks(username: string): Promise<MoxfieldUserDeck[]> {
    return request<MoxfieldUserDeck[]>(`/api/moxfield/user/${encodeURIComponent(username)}`);
  },

  dealSwipeDeck(opts: {
    mode: 'balanced' | 'popular' | 'niche';
    colors?: string;
    theme?: string;
    sets?: string[];
    rarity?: string;
    skipOracleIds?: string[];
  }): Promise<SwipeDeckResponse> {
    const params = new URLSearchParams({ mode: opts.mode });
    if (opts.colors) params.set('colors', opts.colors);
    if (opts.theme) params.set('theme', opts.theme);
    if (opts.sets && opts.sets.length > 0) params.set('sets', opts.sets.join(','));
    if (opts.rarity) params.set('rarity', opts.rarity);
    if (opts.skipOracleIds && opts.skipOracleIds.length > 0) {
      params.set('skipOracleIds', opts.skipOracleIds.join(','));
    }
    return request<SwipeDeckResponse>(`/api/discovery/deck?${params.toString()}`);
  },

  browseCommanders(opts: {
    mode: 'balanced' | 'popular' | 'niche';
    order: 'discovery' | 'color';
    limit: number;
    colors?: string[];
    colorMode?: 'include' | 'exclude';
    theme?: string;
    sets?: string[];
    rarity?: string;
    excludeOracleIds?: string[];
  }): Promise<BrowseResponse> {
    const params = new URLSearchParams({
      mode: opts.mode,
      order: opts.order,
      limit: String(opts.limit),
    });
    if (opts.colors !== undefined) params.set('colors', opts.colors.join(','));
    if (opts.colorMode) params.set('colorMode', opts.colorMode);
    if (opts.theme) params.set('theme', opts.theme);
    if (opts.sets && opts.sets.length > 0) params.set('sets', opts.sets.join(','));
    if (opts.rarity) params.set('rarity', opts.rarity);
    if (opts.excludeOracleIds && opts.excludeOracleIds.length > 0) {
      params.set('excludeOracleIds', opts.excludeOracleIds.join(','));
    }
    return request<BrowseResponse>(`/api/discovery/browse?${params.toString()}`);
  },

  recordVerdict(req: VerdictRequest): Promise<void> {
    return request<void>('/api/discovery/verdict', {
      method: 'POST',
      body: JSON.stringify(req),
    });
  },

  deleteVerdict(oracleId: string): Promise<void> {
    return request<void>(`/api/discovery/verdict/${encodeURIComponent(oracleId)}`, {
      method: 'DELETE',
    });
  },

  listLikedVerdicts(): Promise<CommanderVerdict[]> {
    return request<CommanderVerdict[]>('/api/discovery/verdicts');
  },

  getSwipeThemes(): Promise<GlobalThemesResponse> {
    return request<GlobalThemesResponse>('/api/discovery/themes');
  },

  getCommanderThemes(name: string): Promise<{ themes: string[] }> {
    return request<{ themes: string[] }>(`/api/cards/themes?name=${encodeURIComponent(name)}`);
  },

  // ── Set Review ───────────────────────────────────────────────────────────────

  getSetReviewCards(params: {
    sets: string[];
    colors?: string[];
    rarities?: string[];
  }): Promise<SetReviewCardsResponse> {
    const searchParams = new URLSearchParams({ sets: params.sets.join(',') });
    if (params.colors && params.colors.length > 0) {
      searchParams.set('colors', params.colors.join(','));
    }
    if (params.rarities && params.rarities.length > 0) {
      searchParams.set('rarities', params.rarities.join(','));
    }
    return request<SetReviewCardsResponse>(`/api/set-review/cards?${searchParams.toString()}`);
  },

  getSetReviewSession(): Promise<GetSessionResponse> {
    return request<GetSessionResponse>('/api/set-review/session');
  },

  createSetReviewSession(body: CreateSessionRequest): Promise<void> {
    return request<void>('/api/set-review/session', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },

  patchSetReviewSession(body: PatchSessionRequest): Promise<void> {
    return request<void>('/api/set-review/session', {
      method: 'PATCH',
      body: JSON.stringify(body),
    });
  },

  deleteSetReviewSession(): Promise<void> {
    return request<void>('/api/set-review/session', {
      method: 'DELETE',
    });
  },

  commitSetReviewSession(): Promise<CommitSessionResponse> {
    return request<CommitSessionResponse>('/api/set-review/session/commit', {
      method: 'POST',
    });
  },

  getAllCollectionDecks(): Promise<CollectionDeck[]> {
    return request<CollectionDeck[]>('/api/collection/decks/all');
  },

  /**
   * Fetch token cards associated with a deck's card IDs via Scryfall all_parts.
   * @param cardIds - Scryfall IDs of cards in the deck (up to 100).
   * @returns { tokens: Card[] } — de-duplicated token cards.
   */
  deckTokens(cardIds: string[]): Promise<{ tokens: import('@mtg-deck-tool/shared').Card[] }> {
    return request('/api/playtester/deck-tokens', {
      method: 'POST',
      body: JSON.stringify({ cardIds }),
    });
  },

  /**
   * Resolve horde deck entries (names + token Scryfall IDs) to full Card objects.
   * @param entries - HordeDeckEntry objects from a HordeDeckData.
   * @returns { cards: Card[]; missing: string[] }
   */
  resolveHordeDeck(
    entries: import('@mtg-deck-tool/shared').HordeDeckEntry[],
  ): Promise<{ cards: import('@mtg-deck-tool/shared').Card[]; missing: string[] }> {
    return request('/api/playtester/resolve-horde', {
      method: 'POST',
      body: JSON.stringify({ entries }),
    });
  },
};
